<template>
  <div class="public-questions-screen">
    <div class="content">
      <div class="overlay" :class="{ 'open': isSidebarOpen }" @click.self="closeSidebar">
        <public-questions-sidebar ref="sidebar" :class="{ 'open': isSidebarOpen }"
          :selectedQuestionId="selectedQuestionId" :initialQuestionId="initialQuestionId"
          :initialQuestion="selectedQuestion" @select-question="selectQuestion" />
      </div>

      <div class="question-display">
        <HeaderMenuComponent title="Preguntas Públicas" :permissions="permissions" :subscription="subscription"
          :isMobile="isMobile" @toggle-sidebar="toggleSidebar" @logout="logout" @show-toast="showToast" />

        <div v-if="loading" class="loading-container">
          <div class="loading-spinner"></div>
          <div class="loading-text">Cargando pregunta...</div>
        </div>

        <div v-else-if="!selectedQuestion" class="empty-state">
          <div class="empty-message">Selecciona una pregunta para visualizar</div>
        </div>

        <div v-else class="question-content">
          <div class="messages-container">
            <!-- Load previous messages button -->
            <button v-if="(selectedQuestion.total_messages && selectedQuestion.total_messages > 2) &&
              (hasMorePreviousMessages || previousMessages.length === 0)" class="load-previous-messages"
              @click="loadPreviousMessages" :disabled="loadingPreviousMessages">
              {{ loadingPreviousMessages ? 'Cargando...' : 'Cargar mensajes anteriores' }}
            </button>

            <!-- Previous messages -->
            <div v-if="previousMessages.length > 0">
              <div v-for="message in previousMessages" :key="message._id" class="message-pair">
                <HumanMessageComponent :text="message.q" :userName="'Pregunta'" :timestamp="message.timestamp" />

                <KotBotMessageComponent :text="message.a" :sources="message.sources || []" :qanda_id="message._id"
                  @show-toast="showToast" :has_feedback="message.feedback !== null" :timestamp="message.timestamp"
                  :conversation_id="message.conversation_id" />
              </div>

              <!-- Separator between previous and current messages -->
              <div class="messages-separator" v-if="previousMessages.length > 0">
                <div class="separator-line"></div>
                <div class="separator-text">Mensaje actual</div>
                <div class="separator-line"></div>
              </div>
            </div>

            <!-- Current message -->
            <HumanMessageComponent :text="selectedQuestion.q" :userName="'Pregunta'"
              :timestamp="selectedQuestion.timestamp" />

            <KotBotMessageComponent :text="selectedQuestion.a" :sources="selectedQuestion.sources || []"
              :qanda_id="selectedQuestion._id" @show-toast="showToast"
              :has_feedback="selectedQuestion.feedback !== null" :timestamp="selectedQuestion.timestamp"
              :conversation_id="selectedQuestion.conversation_id" />
          </div>
        </div>
      </div>
    </div>

    <!-- Toast notification -->
    <div v-if="toast.show" class="toast" :class="toast.type">
      {{ toast.message }}
    </div>
  </div>
</template>

<script>
import PublicQuestionsSidebarComponent from '../components/PublicQuestionsSidebarComponent.vue';
import HumanMessageComponent from '../components/HumanMessageComponent.vue';
import KotBotMessageComponent from '../components/KotBotMessageComponent.vue';
import HeaderMenuComponent from '../components/HeaderMenuComponent.vue';
import axios from 'axios';
import { COUNTRY } from '../utils/countryConfig';

export default {
  name: 'PublicQuestionsScreen',
  components: {
    'public-questions-sidebar': PublicQuestionsSidebarComponent,
    HumanMessageComponent,
    KotBotMessageComponent,
    HeaderMenuComponent
  },
  data() {
    return {
      selectedQuestionId: null,
      initialQuestionId: null,
      selectedQuestion: null,
      previousMessages: [],
      loading: false,
      loadingPreviousMessages: false,
      hasMorePreviousMessages: false,
      previousMessagesOffset: 0,
      userName: '',
      userPicture: '',
      permissions: [],
      subscription: null,
      toast: {
        show: false,
        message: '',
        type: 'info',
        timeout: null
      },
      isMobile: false,
      isSidebarOpen: false
    };
  },
  mounted() {
    // Get user info from localStorage
    this.userName = localStorage.getItem('user_name') || '';
    this.userPicture = localStorage.getItem('user_profile_picture') || '';

    // Parse permissions and subscription from localStorage
    try {
      const permissionsData = localStorage.getItem('user_permissions');
      if (permissionsData) {
        this.permissions = JSON.parse(permissionsData);
      }

      const subscriptionData = localStorage.getItem('user_subscription');
      if (subscriptionData) {
        this.subscription = JSON.parse(subscriptionData);
      }
    } catch (error) {
      console.error('Error parsing data from localStorage:', error);
    }

    // Check if there's a question ID in the URL
    const questionId = this.$route.query.id;
    if (questionId) {
      // Set the initialQuestionId to prevent auto-selection in the sidebar
      this.initialQuestionId = questionId;

      // Load the question directly
      this.selectQuestion(questionId);
    }

    // Check if device is mobile
    this.checkMobile();

    // Add resize event listener
    window.addEventListener('resize', this.checkMobile);
  },

  beforeUnmount() {
    // Remove resize event listener
    window.removeEventListener('resize', this.checkMobile);
  },
  methods: {
    async selectQuestion(questionId) {
      if (this.selectedQuestionId === questionId) return;

      this.selectedQuestionId = questionId;
      this.loading = true;

      // Reset previous messages when selecting a new question
      this.resetPreviousMessages();

      // Update URL with the selected question ID
      this.$router.replace({
        query: { ...this.$route.query, id: questionId }
      });

      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_URL}/api/v2/public-questions/get/`, {
          params: {
            qanda_id: questionId,
            country: COUNTRY
          }
        });

        this.selectedQuestion = response.data;

        // If this is the initial question ID, make sure it's set
        if (questionId === this.$route.query.id && !this.initialQuestionId) {
          this.initialQuestionId = questionId;
        }
      } catch (error) {
        console.error('Error loading question details:', error);
        this.selectedQuestion = null;
        this.showToast('Error al cargar la pregunta. Intenta nuevamente más tarde.', 'error');
      } finally {
        this.loading = false;
      }
    },

    async loadPreviousMessages() {
      if (this.loadingPreviousMessages || !this.selectedQuestion) return;

      this.loadingPreviousMessages = true;

      try {
        const response = await axios.get(`${process.env.VUE_APP_BASE_URL}/api/v2/public-questions/previous-messages`, {
          params: {
            conversation_id: this.selectedQuestion.conversation_id,
            question_id: this.selectedQuestion._id,
            limit: 10,
            offset: this.previousMessagesOffset,
            country: COUNTRY
          }
        });

        // Get the response data
        const { messages, has_more } = response.data;

        // Update state
        this.hasMorePreviousMessages = has_more;
        this.previousMessagesOffset += messages.length;

        // Add the new messages to the existing array
        this.previousMessages = [...this.previousMessages, ...messages];

        // Scroll to show the newly loaded messages
        this.$nextTick(() => {
          if (this.previousMessagesOffset === messages.length) { // First load
            const questionContent = document.querySelector('.question-content');
            if (questionContent) {
              questionContent.scrollTop = 0;
            }
          }
        });
      } catch (error) {
        console.error('Error loading previous messages:', error);
        this.showToast('Error al cargar mensajes anteriores. Intenta nuevamente más tarde.', 'error');
      } finally {
        this.loadingPreviousMessages = false;
      }
    },

    resetPreviousMessages() {
      this.previousMessages = [];
      this.previousMessagesOffset = 0;
      this.hasMorePreviousMessages = false;
    },

    showToast(message, type = 'info') {
      // Clear any existing timeout
      if (this.toast.timeout) {
        clearTimeout(this.toast.timeout);
      }

      // Show the toast
      this.toast.show = true;
      this.toast.message = message;
      this.toast.type = type;

      // Set a timeout to hide the toast
      this.toast.timeout = setTimeout(() => {
        this.toast.show = false;
      }, 3000);
    },

    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },

    closeSidebar() {
      this.isSidebarOpen = false;
    },

    toggleSidebar() {
      // Force the sidebar to be visible
      this.isSidebarOpen = !this.isSidebarOpen;

      // Log the state for debugging
      console.log('PublicQuestionsScreen - isSidebarOpen:', this.isSidebarOpen);

      // Force the sidebar to be visible by directly manipulating the DOM if needed
      if (this.isSidebarOpen) {
        const sidebar = this.$refs.sidebar?.$el;
        if (sidebar) {
          sidebar.style.transform = 'translateX(0) !important';
          sidebar.classList.add('open');
        }
      }
    },

    goSuscribirse() {
      this.$router.push({ name: "suscribirse" });
    },

    logout() {
      // Clear user data from localStorage
      localStorage.removeItem('user_email');
      localStorage.removeItem('user_name');
      localStorage.removeItem('user_permissions');
      localStorage.removeItem('user_profile_picture');
      localStorage.removeItem('user_subscription');

      this.showToast('Sesión cerrada exitosamente', 'success');

      // Redirect to login page after a short delay
      setTimeout(() => {
        this.$router.push('/login');
      }, 1000);
    }
  }
}
</script>

<style scoped>
.public-questions-screen {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background-color: #f5f5f5;
  color: #333333;
  width: 100%;
  position: relative;
}

.content {
  display: flex;
  flex: 1;
  overflow: hidden;
  position: relative;
}

.question-display {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #f5f5f5;
}


.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #a1344e;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 16px;
  color: #666666;
}

.empty-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.empty-message {
  font-size: 16px;
  color: #666666;
  text-align: center;
  max-width: 300px;
}

.question-content {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.messages-container {
  max-width: 1200px;
  margin: 0 auto;
}

.load-previous-messages {
  display: block;
  margin: 0 auto 20px;
  padding: 10px 20px;
  background-color: #a1344e;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
}

.load-previous-messages:hover {
  background-color: #b71c1c;
}

.load-previous-messages:disabled {
  background-color: #666;
  cursor: not-allowed;
}

.message-pair, .messages-container {
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
}

.messages-separator {
  display: flex;
  align-items: center;
  margin: 30px 0;
  color: #aaa;
}

.separator-line {
  flex-grow: 1;
  height: 1px;
  background-color: #ddd;
}

.separator-text {
  padding: 0 15px;
  font-size: 14px;
  font-weight: 500;
}

/* Toast notification */
.toast {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 20px;
  border-radius: 4px;
  color: white;
  font-weight: 500;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  min-width: 250px;
  text-align: center;
}

.toast.info {
  background-color: #2196F3;
}

.toast.success {
  background-color: #4CAF50;
}

.toast.warning {
  background-color: #FF9800;
}

.toast.error {
  background-color: #F44336;
}

@media screen and (max-width: 768px) {
  .question-content {
    padding: 15px;
  }

  .messages-container {
    padding: 0;
  }

  .header {
    padding: 12px 15px;
  }

  .header h1 {
    font-size: 16px;
  }

  .overlay {
    transition: background-color 0.3s ease;
  }

  .overlay.open {
    position: fixed;
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100vw;
    height: 100vh;
    z-index: 10;
    background: rgba(0, 0, 0, 0.75);
  }
}
</style>
